import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2, Search, MoreHorizontal, Pause, Play } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import {
  CatalogSearchHit,
  EnrichmentSummary,
  SeedSku,
  getDistributorSeedSkus,
  listEnrichments,
  saveDistributorSeedSkus,
} from '../../services/distributorService';
import { ProductDetailPane } from './ProductDetailPane';

/**
 * "Synced Products" — replaces the old Seed-SKU manager. Lists every SKU
 * the partner has on this distributor's sync list, joined with the
 * enrichment metadata so the table renders as a proper product list
 * (vendor / canonical name / category / status) rather than a raw SKU
 * list.
 *
 * Row-click opens the same ProductDetailPane used in Catalogue browse —
 * read-only here. Edits route to the Marketplace > Products page via
 * the pane's Edit button (handled by the parent's onSwitchToReview /
 * onSwitchToProducts wiring).
 *
 * Inline pause / remove still available behind a per-row ⋮ menu so
 * partners don't lose the ability to manage their list — just less
 * prominent than the old toggle-everywhere layout.
 */
interface SyncedProductsPanelProps {
  distributorId: string;
  distributorLabel: string;
  /** Partner clicks Edit in the pane → parent jumps to Marketplace > Products. */
  onSwitchToProductsPage?: () => void;
  /**
   * Bumped by the parent IngramTab after Run sync — both immediately (to
   * pick up price/stock + status changes on the existing enrichments) and
   * again on every poll tick while the enrichment queue is draining. Without
   * this the table snapshots its enrichment join on mount and never updates,
   * which is why a freshly-synced SKU sat on a stale pending badge for the user
   * even though the worker had already committed a Draft row to SQL.
   */
  refreshKey?: number;
}

export function SyncedProductsPanel({
  distributorId,
  distributorLabel,
  onSwitchToProductsPage,
  refreshKey,
}: SyncedProductsPanelProps) {
  const [serverSkus, setServerSkus] = useState<SeedSku[]>([]);
  const [draft, setDraft] = useState<SeedSku[]>([]);
  const [enrichments, setEnrichments] = useState<EnrichmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newSku, setNewSku] = useState('');
  const [search, setSearch] = useState('');
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [paneIndex, setPaneIndex] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [seed, enr] = await Promise.all([
        getDistributorSeedSkus(distributorId),
        listEnrichments(distributorId).catch(() => null),
      ]);
      setServerSkus(seed.skus ?? []);
      setDraft(seed.skus ?? []);
      setEnrichments(enr?.items ?? []);
      setSelectedSkus(new Set());
    } catch (err: any) {
      setError(err.message ?? 'Failed to load synced products');
    } finally {
      setLoading(false);
    }
  }, [distributorId]);

  useEffect(() => {
    load();
    // refreshKey is intentionally part of the dep list so a sync triggered
    // from the IngramTab header forces a re-fetch — the load callback alone
    // is stable across syncs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, refreshKey]);

  // Index enrichments by SKU for O(1) lookup when joining the seed list.
  const enrichmentBySku = useMemo(() => {
    const m = new Map<string, EnrichmentSummary>();
    for (const e of enrichments) m.set(e.sku, e);
    return m;
  }, [enrichments]);

  const dirty = JSON.stringify(serverSkus) !== JSON.stringify(draft);

  const handleAdd = async () => {
    const sku = newSku.trim();
    if (!sku) return;
    if (draft.some((s) => s.sku === sku)) {
      setError(`${sku} is already in your list`);
      return;
    }
    setError(null);
    setSuccess(null);
    const next = [...draft, { sku, category: null, name: null, enabled: true }];
    setDraft(next);
    setSaving(true);
    try {
      await saveDistributorSeedSkus(distributorId, next);
      setServerSkus(next);
      setNewSku('');
      setSuccess(`${sku} added — pulls in next sync.`);
    } catch (err: any) {
      setError(err.message ?? 'Failed to add SKU');
      setDraft(serverSkus); // revert
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (sku: string) => {
    setOpenMenu(null);
    const next = draft.map((s) => (s.sku === sku ? { ...s, enabled: !s.enabled } : s));
    setDraft(next);
    try {
      await saveDistributorSeedSkus(distributorId, next);
      setServerSkus(next);
    } catch (err: any) {
      setError(err.message ?? 'Failed to toggle SKU');
      setDraft(serverSkus);
    }
  };

  const handleRemove = async (sku: string) => {
    setOpenMenu(null);
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Remove ${sku} from the sync list? Existing enrichment is preserved.`)) return;
    const next = draft.filter((s) => s.sku !== sku);
    setDraft(next);
    try {
      await saveDistributorSeedSkus(distributorId, next);
      setServerSkus(next);
    } catch (err: any) {
      setError(err.message ?? 'Failed to remove SKU');
      setDraft(serverSkus);
    }
  };

  const handleRemoveSelected = async () => {
    const selected = Array.from(selectedSkus);
    if (selected.length === 0) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Remove ${selected.length} SKU${selected.length === 1 ? '' : 's'} from the ${distributorLabel} sync list? Existing enrichment is preserved.`)) return;

    const removeSet = new Set(selected);
    const next = draft.filter((s) => !removeSet.has(s.sku));
    setSaving(true);
    setError(null);
    setSuccess(null);
    setDraft(next);
    setPaneIndex(null);
    setOpenMenu(null);
    try {
      await saveDistributorSeedSkus(distributorId, next);
      setServerSkus(next);
      setSelectedSkus(new Set());
      setSuccess(`Removed ${selected.length} SKU${selected.length === 1 ? '' : 's'} from the sync list.`);
    } catch (err: any) {
      setError(err.message ?? 'Failed to remove selected SKUs');
      setDraft(serverSkus);
    } finally {
      setSaving(false);
    }
  };

  // Build the joined product rows + apply local search filter.
  const rows = useMemo(() => {
    const all = draft.map((s) => {
      const e = enrichmentBySku.get(s.sku);
      return {
        seed: s,
        enrichment: e ?? null,
        // Project into a CatalogSearchHit-compatible shape so the existing
        // ProductDetailPane can mount on row click.
        asHit: {
          sku: s.sku,
          vendorPartNumber: null,
          vendor: e?.vendor ?? null,
          description: e?.canonicalName ?? s.name ?? null,
          category: e?.category ? { l1: e.category, l2: null, l3: null } : null,
          kind: null,
          lifecycle: null,
          isAuthorizedToPurchase: null,
          // price/stock not on the seed/summary; pane fetches live on open.
          price: null,
          rrp: null,
          stock: null,
        } as CatalogSearchHit,
      };
    });
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((r) => {
      const hay = [
        r.seed.sku,
        r.seed.name,
        r.enrichment?.canonicalName,
        r.enrichment?.vendor,
        r.enrichment?.category,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [draft, enrichmentBySku, search]);

  const hits = useMemo(() => rows.map((r) => r.asHit), [rows]);

  const allVisibleSelected = rows.length > 0 && rows.every((r) => selectedSkus.has(r.seed.sku));
  const toggleSelectAllVisible = () => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        rows.forEach((r) => next.delete(r.seed.sku));
      } else {
        rows.forEach((r) => next.add(r.seed.sku));
      }
      return next;
    });
  };
  const toggleSelect = (sku: string) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) {
        next.delete(sku);
      } else {
        next.add(sku);
      }
      return next;
    });
  };

  // Status the badge maps to on the right of each row.
  const statusFor = (sku: string): { label: string; tone: string } => {
    const e = enrichmentBySku.get(sku);
    if (!e) return { label: 'Awaiting enrichment', tone: 'bg-slate-100 text-slate-600 ring-slate-200' };
    if (e.status === 'Active')   return { label: 'Live',       tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200' };
    if (e.status === 'Draft')    return { label: 'In review',  tone: 'bg-amber-50 text-amber-700 ring-amber-200' };
    if (e.status === 'Rejected') return { label: 'Rejected',   tone: 'bg-rose-50 text-rose-700 ring-rose-200' };
    return { label: e.status, tone: 'bg-slate-100 text-slate-600 ring-slate-200' };
  };

  return (
    <div className="px-6 pt-2 pb-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-[18px] font-semibold text-slate-900 tracking-tight">Live catalogue</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {draft.length} SKU{draft.length === 1 ? '' : 's'} you sell, kept in sync with {distributorLabel} on every run. New SKUs trigger AI enrichment for review.
          </p>
        </div>
      </div>

      {/* Add + search */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex items-center gap-2 flex-1 min-w-[280px] max-w-md">
          <input
            type="text"
            placeholder="Add a SKU (e.g. 6693428)"
            value={newSku}
            onChange={(e) => setNewSku(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <Button
            onClick={handleAdd}
            disabled={saving || !newSku.trim()}
            className="inline-flex h-9 flex-shrink-0"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>
        <div className="flex-1" />
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Filter the list…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {selectedSkus.size > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-sm font-medium text-slate-700">
            {selectedSkus.size} selected
          </span>
          <button
            type="button"
            onClick={handleRemoveSelected}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Remove selected
          </button>
          <button
            type="button"
            onClick={() => setSelectedSkus(new Set())}
            disabled={saving}
            className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
      {success && !dirty && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {success}
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="text-sm text-slate-500 inline-flex items-center gap-2 py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading synced products…
        </div>
      ) : draft.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
          <p className="text-sm text-slate-700 font-medium">
            No SKUs on the {distributorLabel} sync list yet
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Add SKUs above, or use Catalogue browse to search and add interactively.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-[11px] text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium py-3 px-4 w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 rounded border-slate-300 text-orange-600"
                    aria-label="Select all visible SKUs"
                  />
                </th>
                <th className="text-left font-medium py-3 px-4 w-28">SKU</th>
                <th className="text-left font-medium py-3 px-4 w-28">Vendor</th>
                <th className="text-left font-medium py-3 px-4">Product</th>
                <th className="text-left font-medium py-3 px-4 w-32">Category</th>
                <th className="text-left font-medium py-3 px-4 w-28">Status</th>
                <th className="text-right font-medium py-3 px-4 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, idx) => {
                const status = statusFor(r.seed.sku);
                const paused = !r.seed.enabled;
                return (
                  <tr
                    key={r.seed.sku}
                    onClick={() => setPaneIndex(idx)}
                    className={`hover:bg-slate-50/60 cursor-pointer ${paused ? 'opacity-60' : ''}`}
                  >
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedSkus.has(r.seed.sku)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelect(r.seed.sku)}
                        className="h-4 w-4 rounded border-slate-300 text-orange-600"
                        aria-label={`Select ${r.seed.sku}`}
                      />
                    </td>
                    <td className="py-3 px-4 font-mono text-xs tabular-nums">
                      <span className="text-orange-600">{r.seed.sku.slice(0, 3)}</span>
                      <span className="text-slate-700">{r.seed.sku.slice(3)}</span>
                    </td>
                    <td className="py-3 px-4 inline-flex items-center gap-1.5">
                      <VendorBadge vendor={r.enrichment?.vendor ?? null} />
                      <span className="text-sm text-slate-700">
                        {r.enrichment?.vendor ?? <span className="text-slate-400">—</span>}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-slate-900 truncate max-w-md">
                        {r.enrichment?.canonicalName ?? r.seed.name ?? <span className="text-slate-400 italic">Pending enrichment</span>}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">
                      {r.enrichment?.category ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${status.tone} ${paused ? 'opacity-70' : ''}`}>
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                        {paused ? 'Paused' : status.label}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right relative">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === r.seed.sku ? null : r.seed.sku); }}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-500 hover:bg-slate-100"
                        title="More actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                      {openMenu === r.seed.sku && (
                        <div
                          className="absolute right-2 top-9 z-10 bg-white border border-slate-200 rounded-md shadow-lg py-1 min-w-[160px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => handleToggle(r.seed.sku)}
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 inline-flex items-center gap-2"
                          >
                            {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                            {paused ? 'Resume sync' : 'Pause sync'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemove(r.seed.sku)}
                            className="w-full text-left px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 inline-flex items-center gap-2"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove from list
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail pane */}
      {paneIndex !== null && hits[paneIndex] && (
        <ProductDetailPane
          results={hits}
          index={paneIndex}
          distributorId={distributorId}
          distributorLabel={distributorLabel}
          isInSeed={true}
          adding={false}
          // Cached mode: this SKU is already on the Synced Products list, so
          // row clicks should not probe live Ingram. If an enrichment row is
          // known, the server can also hand back last-sync price/stock from
          // the local cache.
          cached={true}
          hasKnownEnrichment={!!rows[paneIndex]?.enrichment}
          onClose={() => setPaneIndex(null)}
          onPrev={() => setPaneIndex((i) => (i !== null && i > 0 ? i - 1 : i))}
          onNext={() => setPaneIndex((i) => (i !== null && i < hits.length - 1 ? i + 1 : i))}
          onAdd={() => { /* already in seed — Add button shows "In sync list" */ }}
          onEdit={() => {
            // Per design intent: editing happens on the Marketplace > Products
            // page, not inline. Bounce up to the parent to navigate.
            onSwitchToProductsPage?.();
            setPaneIndex(null);
          }}
        />
      )}
    </div>
  );
}

// ─── small components ────────────────────────────────────────────────

function VendorBadge({ vendor }: { vendor: string | null }) {
  const label = (vendor ?? '??').slice(0, 2).toUpperCase();
  const map: Record<string, string> = {
    LE: 'bg-red-500 text-white',
    HP: 'bg-blue-500 text-white',
    DE: 'bg-cyan-600 text-white',
    CI: 'bg-blue-700 text-white',
    FO: 'bg-rose-600 text-white',
    MI: 'bg-emerald-600 text-white',
    AP: 'bg-slate-700 text-white',
    AR: 'bg-orange-500 text-white',
    LO: 'bg-violet-500 text-white',
  };
  const colour = map[label] ?? 'bg-slate-400 text-white';
  return (
    <span className={`inline-flex items-center justify-center h-5 w-5 rounded text-[9px] font-bold flex-shrink-0 ${colour}`}>
      {label}
    </span>
  );
}
